import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, connect, useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { Typography, Button, TextField } from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';
import * as PublicStore from '../../app/store/PublicStore';
import { validateAllFields, validateEmail, validateRequired } from '../../youwell-common/validationUtils';

const useStyles = makeStyles(theme => ({
    nameAndEmail: {
        display: 'flex',
        flexDirection: 'row',
        '& > div': {
            margin: theme.spacing(1),
        },
    },
    submitButtonWrapper: {
        marginTop: theme.spacing(2),
        display: 'flex',
        alignItems: 'flex-end',
        flexDirection: 'column',
    },
    submitButton: {
        margin: theme.spacing(1),
        width: 240,
    },
    header: {
        margin: theme.spacing(1, 0),
    },
    description: {
        margin: theme.spacing(1, 0, 4),
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
    },
    responseMsg: {
        margin: theme.spacing(1),
    },
}));

const validators = {
    email: validateEmail,
    description: validateRequired,
};

const initalFormState = () => ({
    name: '', email: '', description: '',
});

const FeedbackForm = ({
    translate,
}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const formRef = useRef();

    const appConfig = useSelector(state => state.pageSettings.appConfig);
    const profileData = useSelector(state => state.auth.profileData);
    const [formState, setFormState] = useState(initalFormState());

    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isFormValid, setIsFormValid] = useState(true);
    const [feedbackFormSent, setFeedbackFormSent] = useState(false);

    const handleFormChange = name => (e) => {
        setFormState({ ...formState, [name]: e.target.value });
    };

    useEffect(() => {
        if (formState.name || formState.email || formState.description) {
            setFeedbackFormSent(false);
        }
        const validationErrors = validateAllFields(formState, validators);
        setIsEmailValid(!validationErrors.email);
        setIsFormValid(!validationErrors.description);
    }, [formState]);

    const handleSubmitForm = (e) => {
        e.preventDefault();
        const feedbackData = {
            ...formState,
            organizationId: appConfig?.orgId || profileData?.organization?.id,
            app: appConfig?.appName,
        };
        dispatch(PublicStore.actionCreators.sendFeedbackFromForm(feedbackData, { callback: () => {
            setFormState(initalFormState());
            formRef.current.reset();
            setFeedbackFormSent(true);
        } }));
    };

    const includeNameAndEmail = false;

    return (
        <form onSubmit={handleSubmitForm} ref={formRef}>
            <div>
                <Typography className={classes.header} variant="h6">
                    {translate('feedback.title')}
                </Typography>

                <Typography className={classes.description} variant="body1">
                    {translate('feedback.description')}
                </Typography>

                {includeNameAndEmail && (
                    <div className={classes.nameAndEmail}>
                        <TextField onChange={handleFormChange('name')} label={translate('feedback.name')} variant="outlined" />
                        <TextField error={!isEmailValid} onBlur={handleFormChange('email')} label={translate('feedback.email')} variant="outlined" />
                    </div>
                )}

                <TextField
                    fullWidth
                    onChange={handleFormChange('description')}
                    required
                    label={translate('feedback.yourMessage')}
                    variant="outlined"
                    multiline
                    minRows={7}
                />

                <div className={classes.submitButtonWrapper}>
                    <Button color="secondary" disabled={!isFormValid} type="submit" variant="outlined" className={classes.submitButton}>{translate('feedback.submitMessage')}</Button>
                    <Typography className={classes.responseMsg}>{feedbackFormSent && translate('feedback.messageSubmitted')}</Typography>
                </div>
            </div>
        </form>
    );
};

FeedbackForm.propTypes = {
    translate: PropTypes.func.isRequired,
};

FeedbackForm.defaultProps = {};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
});

export default connect(mapStateToProps)(FeedbackForm);
